import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Plus, CreditCard, Building, TrendingUp, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function SmsCreditAllocationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [filteredClinics, setFilteredClinics] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClinic, setSelectedClinic] = useState('');
  const [creditsToAdd, setCreditsToAdd] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadClinics();
  }, []);

  useEffect(() => {
    // Filter and sort clinics based on search query
    if (searchQuery.trim() === '') {
      setFilteredClinics(clinics);
    } else {
      const filtered = clinics.filter(clinic =>
        clinic.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredClinics(filtered);
    }
  }, [searchQuery, clinics]);

  const loadClinics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/clinics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure each clinic has an id field (use _id if id is missing)
        const clinicsWithIds = data.map(clinic => ({
          ...clinic,
          id: clinic.id || clinic._id
        }));
        // Sort alphabetically by name
        const sortedClinics = clinicsWithIds.sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        setClinics(sortedClinics);
        setFilteredClinics(sortedClinics);
      }
    } catch (error) {
      console.error('Failed to load clinics:', error);
      toast.error('Failed to load clinics');
    } finally {
      setLoading(false);
    }
  };

  const handleAllocateCredits = async (e) => {
    e.preventDefault();
    
    if (!selectedClinic) {
      toast.error('Please select a clinic');
      return;
    }

    const credits = parseInt(creditsToAdd);
    if (isNaN(credits) || credits <= 0) {
      toast.error('Please enter a valid number of credits');
      return;
    }

    setAllocating(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sms/credits/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clinic_id: selectedClinic,
          credits_to_add: credits,
          reason: reason || 'Credit allocation by superadmin'
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Successfully added ${credits} credits. New balance: ${data.new_balance}`);
        setCreditsToAdd('');
        setReason('');
        setDialogOpen(false);
        loadClinics(); // Reload to see updated balances
      } else {
        toast.error(data.detail || 'Failed to allocate credits');
      }
    } catch (error) {
      console.error('Error allocating credits:', error);
      toast.error('Failed to allocate credits');
    } finally {
      setAllocating(false);
    }
  };

  const getClinicBalance = (clinicId) => {
    const clinic = clinics.find(c => c.id === clinicId);
    return clinic?.sms_credits || 0;
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="p-6">
        <Alert>
          <AlertDescription>
            You don't have permission to access credit allocation.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: "#652D90" }}>SMS Credit Allocation</h1>
            <p className="text-gray-600 mt-2">Manage SMS credits for clinics</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} style={{ backgroundColor: "#652D90" }}>
            <Plus className="mr-2 h-4 w-4" />
            Add Credits
          </Button>
        </div>

      {/* Search Filter */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <Input
          type="text"
          placeholder="Search clinic by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Clinics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {loading ? (
          <div className="col-span-3 flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : filteredClinics.length === 0 ? (
          <div className="col-span-3 text-center py-8 text-gray-500">
            {searchQuery ? 'No clinics found matching your search' : 'No clinics found'}
          </div>
        ) : (
          filteredClinics.map((clinic) => (
            <Card key={clinic.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building size={20} style={{ color: "#652D90" }} />
                  {clinic.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CreditCard size={24} style={{ color: "#652D90" }} />
                      <span className="text-sm text-gray-600">SMS Credits</span>
                    </div>
                    <span className="text-2xl font-bold" style={{ color: "#652D90" }}>
                      {clinic.sms_credits || 0}
                    </span>
                  </div>
                  
                  <div className="text-xs text-gray-500 mt-2">
                    <p><strong>Email:</strong> {clinic.email || 'N/A'}</p>
                    <p><strong>Phone:</strong> {clinic.phones?.[0] || 'N/A'}</p>
                    <p className="text-xs text-gray-400 mt-1"><strong>ID:</strong> {clinic.id}</p>
                  </div>
                  
                  <Button
                    onClick={() => {
                      setSelectedClinic(clinic.id);
                      setDialogOpen(true);
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                  >
                    <TrendingUp size={14} className="mr-2" />
                    Add Credits
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Credits Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add SMS Credits</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAllocateCredits} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clinic">Select Clinic</Label>
              <Select value={selectedClinic} onValueChange={setSelectedClinic}>
                <SelectTrigger id="clinic">
                  <SelectValue placeholder="Choose a clinic" />
                </SelectTrigger>
                <SelectContent>
                  {clinics.length === 0 ? (
                    <SelectItem value="no-clinics" disabled>No clinics available</SelectItem>
                  ) : (
                    clinics.map((clinic) => (
                      <SelectItem key={clinic.id} value={clinic.id || clinic._id || `clinic-${Math.random()}`}>
                        {clinic.name} (Current: {clinic.sms_credits || 0} credits)
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="credits">Number of Credits to Add</Label>
              <Input
                id="credits"
                type="number"
                min="1"
                value={creditsToAdd}
                onChange={(e) => setCreditsToAdd(e.target.value)}
                placeholder="Enter number of credits"
                required
              />
              <p className="text-sm text-gray-500">
                1 Credit = 1 SMS segment
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Input
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Monthly allocation, top-up, etc."
              />
            </div>

            {selectedClinic && creditsToAdd && (
              <Alert>
                <AlertDescription>
                  <strong>Summary:</strong> Add {creditsToAdd} credits to {clinics.find(c => c.id === selectedClinic)?.name}
                  <br />
                  <strong>New Balance:</strong> {getClinicBalance(selectedClinic) + parseInt(creditsToAdd || 0)} credits
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDialogOpen(false);
                  setCreditsToAdd('');
                  setReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={allocating || !selectedClinic || !creditsToAdd}
                style={{ backgroundColor: "#652D90" }}
              >
                {allocating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Credits
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Credit System Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-600">
          <p><strong>Credit Model:</strong> 1 Credit = 1 SMS segment</p>
          <p><strong>Segment Calculation:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Normal Text: 160 characters per credit</li>
            <li>Unicode/Nepali: 70 characters per credit</li>
          </ul>
          <p className="mt-3"><strong>Usage Tracking:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Credits are deducted only on successful message delivery</li>
            <li>Invalid phone numbers are filtered out before sending (no charge)</li>
            <li>All transactions are audited and logged</li>
          </ul>
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
}
